import { useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { api, ApiError, type JatkuvuusRivi } from "./api";
import { Tilasana } from "./Tila";
import { Ohje } from "./Ohje";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════
   Hitsauksen jatkuvuus — «viimeksi tehty» (T350, taso 1).

   Todistusrekisteri tietää voimassaolopäivän. TAUON TIETÄÄ VAIN LOKI:
   milloin tämä ihminen viimeksi kirjasi sauman tällä menetelmällä.
   Excel, mappi ja pätevyysrekisterit eivät tiedä sitä, koska ne eivät
   pidä kirjaa työstä.

   ⛔ Tämä paneeli EI väitä mitään pätevyyden voimassaolosta eikä lue
   pätevyysrekisteriä. Se kertoo mitä lokissa lukee. Pätevyyden ja
   menetelmän yhdistäminen on oma päätöksensä (migraatio 024).

   ⛔ Sääntö 61: päivätön sauma ei ole vanha sauma. Jos kaikki parin
   saumat ovat päivättömiä, rivi sanoo «ei mitattavissa» eikä näytä
   pitkältä tauolta — ne ovat eri asioita ja näyttävät eriltä.
   ═══════════════════════════════════════ */

export default function Jatkuvuus() {
  const [rivit, setRivit] = useState<JatkuvuusRivi[] | null>(null);
  const [hetki, setHetki] = useState<string | null>(null);
  const [piilota, setPiilota] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  useEffect(() => {
    let elossa = true;
    void (async () => {
      try {
        const d = await api.hitsauksenJatkuvuus();
        if (!elossa) return;
        setRivit(d.rivit); setHetki(d.hetki);
      } catch (e) {
        if (!elossa) return;
        // Moduuli pois päältä = lohkoa ei ole, ei virhe. Muu virhe näkyy.
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) setPiilota(true);
        else setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("jat.lukuvirhe"));
      }
    })();
    return () => { elossa = false; };
  }, []);

  if (piilota) return null;
  if (virhe) return <section className="panel"><p className="virhe">{virhe}</p></section>;
  if (!rivit) return null;

  const tasmalliset = rivit.filter((r) => r.tarkkuus === "tasmallinen").length;
  const ylarajat = rivit.filter((r) => r.tarkkuus === "ylaraja").length;
  const eiMitattavat = rivit.filter((r) => r.tarkkuus === "ei_mitattavissa").length;

  return (
    <section className="panel" data-jatkuvuus="">
      <div className="panel__head">
        <h2>{t("jat.otsikko")}</h2>
      </div>
      {/* Ohje ylös laatikkona (Toni 24.9.2026): sama sisältö, joka oli otsikon vieressä ja taulukon alla pienellä. */}
      <Ohje
        kuka={<>{t("jat.ohje.kuka")}</>}
        luvut={<>
          {rivit.length > 0 && <>{t("jat.parit", { n: rivit.length, m: tasmalliset })}
            {ylarajat > 0 && t("jat.ylarajat", { n: ylarajat })}
            {eiMitattavat > 0 && t("jat.ei_paivattyja", { n: eiMitattavat })}.
            {hetki && t("jat.mitattu", { pvm: naytaPvm(hetki) ?? "" })}{" "}</>}
          {t("jat.ohje.ylaraja")}{" "}
          <b>{t("jat.ohje.raukeaa")}</b>{" "}
          {t("jat.ohje.varit")}</>} />

      {rivit.length === 0
        ? <p>{t("jat.ei_mitattavaa")}</p>
        : (
          <>
            <table className="taulu">
              <thead>
                <tr>
                  <th className="th--vasen">{t("jat.henkilo")}</th>
                  <th className="th--vasen">{t("jat.menetelma")}</th>
                  <th className="th--vasen">{t("jat.viimeksi")}</th>
                  <th className="th--oikea">{t("jat.paivia")}</th>
                  <th className="th--oikea">{t("jat.saumoja")}</th>
                  <th className="th--vasen">{t("jat.huomiot")}</th>
                </tr>
              </thead>
              <tbody data-rivit={rivit.length}>
                {rivit.map((r) => (
                  <tr key={`${r.henkilo_id}-${r.prosessi}`} data-jatkuvuus-rivi="">
                    <td>{r.nimi}{r.tyotunnus ? <span className="dim"> · {r.tyotunnus}</span> : null}</td>
                    <td className="mono">{r.prosessi}</td>
                    <td>{r.viimeisin ? <span>{naytaPvm(r.viimeisin)}</span> : <span className="dim">—</span>}</td>
                    <td className="num" data-paivia={r.paivia ?? ""} data-tarkkuus={r.tarkkuus} data-kk-raja={r.kk_raja ?? ""}>
                      {r.tarkkuus === "ei_mitattavissa"
                        ? <Tilasana laji="ei_mitattu">{t("jat.ei_mitattavissa")}</Tilasana>
                        : r.tarkkuus === "ylaraja"
                          ? <>≤&nbsp;{r.paivia}<span className="dim"> {t("jat.pv")}</span></>
                          : <>{r.paivia}<span className="dim"> {t("jat.pv")}</span></>}
                      {/* Päätös 88: tila sanana ja värinä (Tila.tsx) — punainen yli 6 kk, keltainen yli 5 kk, «ehkä» päiväämättömillä. */}
                      {r.kk_raja === "yli_6" && <> <Tilasana laji="vika">{t("jat.yli_6")}</Tilasana></>}
                      {r.kk_raja === "yli_5" && <> <Tilasana laji="varoitus">{t("jat.yli_5")}</Tilasana></>}
                      {r.kk_raja === "ehka_yli_6" && <> <Tilasana laji="varoitus">{t("jat.ehka_yli_6")}</Tilasana></>}
                      {r.kk_raja === "ehka_yli_5" && <> <Tilasana laji="varoitus">{t("jat.ehka_yli_5")}</Tilasana></>}
                    </td>
                    <td className="num">{r.hitseja}</td>
                    <td>
                      {r.tarkkuus === "ylaraja" && (
                        <Tilasana laji="varoitus">
                          {t("jat.lisaksi_paivattomia", { n: r.paivattomia })}
                        </Tilasana>
                      )}
                      {r.epatarkkoja > 0 && (
                        <span className="dim"> {t("jat.epatarkkoja", { n: r.epatarkkoja })}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
    </section>
  );
}
